import { useState, useEffect, useRef, useCallback } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { Calendar, Clock, Users, ChevronLeft, ChevronRight, Loader2, Check, Phone, Mail, MessageSquare, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogBody, DialogFooterSticky } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { useSwipeSteps } from '@/hooks/useSwipeSteps';

import { API } from '@/config/api';
import TableFloorPicker from './TableFloorPicker';

// OTP Input Component with auto-focus
function OTPInput({ value, onChange, disabled }) {
  const inputRefs = useRef([]);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleChange = (index, digit) => {
    if (!/^\d*$/.test(digit)) return; // Only allow digits
    
    const newOtp = [...otp];
    newOtp[index] = digit.slice(-1); // Take only last character
    setOtp(newOtp);
    onChange(newOtp.join(''));
    
    // Auto-focus next input
    if (digit && index < 5 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = pastedData.split('').concat(Array(6).fill('')).slice(0, 6);
    setOtp(newOtp);
    onChange(newOtp.join(''));
    
    // Focus appropriate input
    const focusIndex = Math.min(pastedData.length, 5);
    if (inputRefs.current[focusIndex]) {
      inputRefs.current[focusIndex].focus();
    }
  };

  return (
    <div className="flex gap-2 justify-center">
      {otp.map((digit, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className={cn(
            "w-12 h-14 text-center text-2xl font-bold border-2 rounded-lg",
            "focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none",
            "transition-all duration-200",
            digit ? "border-primary bg-primary/5" : "border-gray-300",
            disabled && "opacity-50 cursor-not-allowed"
          )}
        />
      ))}
    </div>
  );
}

// Proof that this device verified the customer's phone once (sent on later bookings)
const VERIFICATION_KEY = 'c29_verified_phone_token';

export default function TableReservationWidget({ restaurantId, restaurantName }) {
  // Set when the customer picks a table on the floor plan; otherwise the restaurant assigns one
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: Date/Time, 2: Contact Info, 3: Review, 4: OTP Verify, 5: Confirmation
  const [stepDirection, setStepDirection] = useState('next'); // 'next' or 'prev' for animation
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [partySize, setPartySize] = useState(2);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  
  // Contact info
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');
  
  // OTP state
  const [reservationId, setReservationId] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [phoneLast4, setPhoneLast4] = useState('');
  
  // Confirmation
  const [confirmationCode, setConfirmationCode] = useState(null);
  const [confirmedReservation, setConfirmedReservation] = useState(null);

  // Step navigation helpers with validation
  const canGoNext = useCallback(() => {
    if (step === 1) return selectedDate && selectedTime;
    if (step === 2) return customerName && customerPhone;
    if (step === 3) return true; // Review step - can always proceed to submit
    return false;
  }, [step, selectedDate, selectedTime, customerName, customerPhone]);

  const goToNextStep = useCallback(() => {
    if (step < 4 && canGoNext()) {
      setStepDirection('next');
      setStep(prev => prev + 1);
    }
  }, [step, canGoNext]);

  const goToPrevStep = useCallback(() => {
    if (step > 1 && step < 5) {
      setStepDirection('prev');
      setStep(prev => prev - 1);
    }
  }, [step]);

  // Swipe gesture hook - enabled for steps 1-3 (not OTP or confirmation)
  const { touchHandlers } = useSwipeSteps({
    onNext: goToNextStep,
    onPrev: goToPrevStep,
    enabled: step >= 1 && step <= 3,
    threshold: 60
  });

  // Opening the widget on the date step builds the date strip (the step is read, not watched).
  const datesOnOpen = useLatestCallback(() => {
    if (step === 1) generateDates();
  });
  useEffect(() => {
    if (isOpen) datesOnOpen();
  }, [isOpen, datesOnOpen]);

  // Resend countdown timer
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  // Auto-submit OTP when 6 digits entered (the code is watched; the step is read)
  const verifyOnOtpStep = useLatestCallback(() => {
    if (step === 4) handleVerifyOTP();
  });
  useEffect(() => {
    if (otpCode.length === 6) verifyOnOtpStep();
  }, [otpCode, verifyOnOtpStep]);

  const generateDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() + i);
      dates.push({
        date: date.toISOString().split('T')[0],
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNum: date.getDate(),
        month: date.toLocaleDateString('en-US', { month: 'short' }),
        isToday: i === 0,
        isTomorrow: i === 1
      });
    }
    setAvailableDates(dates);
    if (!selectedDate) {
      setSelectedDate(dates[0].date);
    }
  };

  const fetchTimeSlots = useLatestCallback(async (date) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/reservations/available-slots/${restaurantId}?date=${date}&party_size=${partySize}`
      );
      setAvailableSlots(response.data.slots || []);
    } catch (error) {
      // Never offer times the restaurant did not confirm are free
      setAvailableSlots([]);
      toast.error('Could not load available times. Please try again.');
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    if (selectedDate) {
      fetchTimeSlots(selectedDate);
    }
  }, [selectedDate, partySize, fetchTimeSlots]);

  const handleSubmit = async () => {
    if (!customerName || !customerPhone) {
      toast.error('Please fill in your name and phone number');
      return;
    }

    setSubmitting(true);
    try {
      // A phone verified on this device before books without a new code
      const savedToken = (() => { try { return localStorage.getItem(VERIFICATION_KEY); } catch { return null; } })();
      const response = await axios.post(`${API}/reservations/`, {
        restaurant_id: restaurantId,
        customer_name: customerName,
        customer_phone: customerPhone,
        customer_email: customerEmail || null,
        party_size: partySize,
        date: selectedDate,
        time: selectedTime,
        special_requests: specialRequests || null,
        table_id: selectedTableId || null,
      }, savedToken ? { headers: { 'X-Customer-Verification': savedToken } } : {});

      // Check if OTP verification is required
      if (response.data.requires_verification) {
        setReservationId(response.data.reservation.id);
        setPhoneLast4(response.data.reservation.phone_last_4);
        setResendCountdown(60); // Start countdown for resend
        setStep(4); // Go to OTP verification step
        toast.success('Verification code sent to your phone!');
      } else {
        // Phone already verified on this device
        setConfirmationCode(response.data.reservation.confirmation_code);
        setConfirmedReservation(response.data.reservation);
        setStep(5);
        toast.success(response.data.message || 'Reservation confirmed!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to make reservation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otpCode.length !== 6) {
      setOtpError('Please enter the 6-digit code');
      return;
    }

    setVerifying(true);
    setOtpError('');
    
    try {
      const response = await axios.post(`${API}/reservations/verify-otp`, {
        reservation_id: reservationId,
        otp_code: otpCode
      });
      
      if (response.data.success) {
        if (response.data.verification_token) {
          try { localStorage.setItem(VERIFICATION_KEY, response.data.verification_token); } catch { /* private mode */ }
        }
        setConfirmationCode(response.data.reservation.confirmation_code);
        setConfirmedReservation(response.data.reservation);
        setStep(5);
        toast.success('Reservation confirmed!');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Invalid verification code';
      setOtpError(errorMsg);
      setOtpCode('');
      toast.error(errorMsg);
    } finally {
      setVerifying(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCountdown > 0) return;
    
    setResending(true);
    try {
      await axios.post(`${API}/reservations/resend-otp`, {
        reservation_id: reservationId
      });
      setResendCountdown(60);
      setOtpCode('');
      setOtpError('');
      toast.success('New verification code sent!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to resend code';
      toast.error(errorMsg);
    } finally {
      setResending(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSelectedDate(null);
    setSelectedTime(null);
    setPartySize(2);
    setCustomerName('');
    setCustomerPhone('');
    setCustomerEmail('');
    setSpecialRequests('');
    setConfirmationCode(null);
    setConfirmedReservation(null);
    setReservationId(null);
    setOtpCode('');
    setOtpError('');
    setPhoneLast4('');
    setResendCountdown(0);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) resetForm(); }}>
      <DialogTrigger asChild>
        <Button size="lg" className="gap-2">
          <Calendar className="h-5 w-5" />
          Reserve a Table
        </Button>
      </DialogTrigger>
      
      <DialogContent className="max-w-md">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            {step === 4 ? (
              <>
                <MessageSquare className="h-5 w-5 text-primary" />
                Verify Your Phone
              </>
            ) : step === 5 ? (
              <>
                <Check className="h-5 w-5 text-green-600" />
                Reservation Confirmed!
              </>
            ) : (
              <>
                <Calendar className="h-5 w-5 text-primary" />
                Reserve a Table
              </>
            )}
          </DialogTitle>
          {step < 4 && (
            <p className="text-sm text-muted-foreground">{restaurantName}</p>
          )}
        </DialogHeader>

        <DialogBody 
          {...touchHandlers}
          className="swipeable-content"
          key={step} // Re-mount on step change for animation
        >

        {/* Progress indicator with swipe hint */}
        {step < 5 && (
          <div className="flex gap-2 mb-4">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={cn(
                  "flex-1 h-1 rounded-full transition-all duration-300 swipe-indicator",
                  step >= s ? "bg-primary" : "bg-muted",
                  step === s && "active"
                )}
              />
            ))}
          </div>
        )}

        {/* Step 1: Date & Time Selection */}
        {step === 1 && (
          <div className="space-y-4">
            {/* Party Size Quick Select */}
            <div>
              <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                <Users className="h-4 w-4" />
                Party Size
              </label>
              <div className="flex gap-2 flex-wrap">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((size) => (
                  <Button
                    key={size}
                    variant={partySize === size ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPartySize(size)}
                    className="w-10 h-10"
                  >
                    {size}
                  </Button>
                ))}
                <Button
                  variant={partySize > 8 ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPartySize(10)}
                  className="px-3 h-10"
                >
                  9+
                </Button>
              </div>
            </div>

            {/* Date Selection */}
            <div>
              <label className="text-sm font-medium mb-2 block">Select Date</label>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {availableDates.map((d) => (
                  <Button
                    key={d.date}
                    variant={selectedDate === d.date ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedDate(d.date)}
                    className="flex-shrink-0 flex-col h-auto py-2 min-w-[60px]"
                  >
                    <span className="text-xs">{d.dayName}</span>
                    <span className="text-lg font-bold">{d.dayNum}</span>
                    <span className="text-xs">{d.month}</span>
                    {d.isToday && <Badge variant="secondary" className="text-[10px] mt-1">Today</Badge>}
                  </Button>
                ))}
              </div>
            </div>

            {/* Time Selection */}
            <div>
              <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Select Time
              </label>
              {loading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                  {availableSlots.map((slot) => (
                    <Button
                      key={slot.time}
                      variant={selectedTime === slot.time ? 'default' : 'outline'}
                      size="sm"
                      disabled={!slot.available}
                      onClick={() => setSelectedTime(slot.time)}
                      className={cn(!slot.available && "opacity-50")}
                    >
                      {slot.time}
                    </Button>
                  ))}
                </div>
              )}
              {selectedDate && selectedTime && (
                <TableFloorPicker restaurantId={restaurantId} date={selectedDate} time={selectedTime}
                  partySize={partySize} selectedTableId={selectedTableId} onSelect={setSelectedTableId} />
              )}
            </div>
          </div>
        )}

        {/* Step 2: Contact Info */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-3 text-sm">
              <p className="font-medium">{formatDate(selectedDate)}</p>
              <p className="text-muted-foreground">
                {selectedTime} • {partySize} {partySize === 1 ? 'guest' : 'guests'}
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">Name *</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Your full name"
                  className="w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  required
                />
              </div>
              
              <div>
                <label className="text-sm font-medium flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  Phone * <span className="text-xs text-muted-foreground">(for verification)</span>
                </label>
                <input
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className="w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  We'll send a verification code to confirm your reservation
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium">Email (optional)</label>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Special Requests</label>
                <textarea
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  placeholder="Allergies, accessibility needs, special occasion..."
                  rows={2}
                  className="w-full mt-1 px-3 py-2 border rounded-lg resize-none focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Review & Confirm */}
        {step === 3 && (
          <div className="space-y-4">
            <Card>
              <CardContent className="pt-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Restaurant</span>
                  <span className="font-medium">{restaurantName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date</span>
                  <span className="font-medium">{formatDate(selectedDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Time</span>
                  <span className="font-medium">{selectedTime}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Party Size</span>
                  <span className="font-medium">{partySize} {partySize === 1 ? 'guest' : 'guests'}</span>
                </div>
                <hr />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Name</span>
                  <span className="font-medium">{customerName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Phone</span>
                  <span className="font-medium">{customerPhone}</span>
                </div>
                {customerEmail && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Email</span>
                    <span className="font-medium">{customerEmail}</span>
                  </div>
                )}
                {specialRequests && (
                  <div>
                    <span className="text-muted-foreground text-sm">Special Requests</span>
                    <p className="text-sm mt-1 italic">&ldquo;{specialRequests}&rdquo;</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
              <p className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                A verification code will be sent to your phone
              </p>
            </div>
          </div>
        )}

        {/* Step 4: OTP Verification */}
        {step === 4 && (
          <div className="space-y-6 text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
              <MessageSquare className="h-8 w-8 text-primary" />
            </div>
            
            <div>
              <p className="text-lg font-semibold">Enter Verification Code</p>
              <p className="text-muted-foreground text-sm mt-1">
                We sent a 6-digit code to the phone ending in <span className="font-medium">****{phoneLast4}</span>
              </p>
            </div>
            
            <OTPInput
              value={otpCode}
              onChange={setOtpCode}
              disabled={verifying}
            />
            
            {otpError && (
              <p className="text-red-500 text-sm">{otpError}</p>
            )}
            
            <p className="text-xs text-muted-foreground">
              Code expires in 5 minutes. Didn&apos;t receive it? Check your SMS inbox.
            </p>
          </div>
        )}

        {/* Step 5: Confirmation */}
        {step === 5 && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            
            <div>
              <p className="text-lg font-semibold">Your table is reserved!</p>
              <p className="text-muted-foreground">
                Confirmation #{confirmationCode}
              </p>
            </div>
            
            <Card>
              <CardContent className="pt-4 text-left space-y-2">
                <p className="font-medium">{confirmedReservation?.restaurant_name || restaurantName}</p>
                <p className="text-sm text-muted-foreground">
                  {formatDate(selectedDate)} at {selectedTime}
                </p>
                <p className="text-sm text-muted-foreground">
                  {partySize} {partySize === 1 ? 'guest' : 'guests'}
                </p>
                {confirmedReservation?.table_number && (
                  <p className="text-sm text-muted-foreground">
                    Table: {confirmedReservation.table_number}
                  </p>
                )}
              </CardContent>
            </Card>
            
            <p className="text-sm text-muted-foreground">
              We&apos;ve sent confirmation details to your phone
              {customerEmail && ' and email'}.
            </p>
          </div>
        )}
        </DialogBody>

        {/* Sticky Footer with Action Buttons */}
        <DialogFooterSticky>
          {step === 1 && (
            <Button
              className="w-full"
              disabled={!selectedDate || !selectedTime}
              onClick={() => { setStepDirection('next'); setStep(2); }}
            >
              Continue
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
          
          {step === 2 && (
            <div className="flex gap-2 w-full">
              <Button variant="outline" onClick={() => { setStepDirection('prev'); setStep(1); }} className="flex-1">
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <Button 
                onClick={() => { setStepDirection('next'); setStep(3); }} 
                className="flex-1"
                disabled={!customerName || !customerPhone}
              >
                Review
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
          
          {step === 3 && (
            <div className="flex gap-2 w-full">
              <Button variant="outline" onClick={() => { setStepDirection('prev'); setStep(2); }} className="flex-1">
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <Button 
                onClick={handleSubmit} 
                className="flex-1"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Sending Code...
                  </>
                ) : (
                  <>Send Verification Code</>
                )}
              </Button>
            </div>
          )}
          
          {step === 4 && (
            <div className="space-y-3 w-full">
              <Button
                onClick={handleVerifyOTP}
                className="w-full"
                disabled={otpCode.length !== 6 || verifying}
              >
                {verifying ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Verifying...
                  </>
                ) : (
                  'Verify & Confirm'
                )}
              </Button>
              
              <Button
                variant="ghost"
                onClick={handleResendOTP}
                disabled={resendCountdown > 0 || resending}
                className="w-full text-sm"
              >
                {resending ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin mr-2" />
                    Sending...
                  </>
                ) : resendCountdown > 0 ? (
                  <>Resend code in {resendCountdown}s</>
                ) : (
                  <>
                    <RefreshCw className="h-3 w-3 mr-2" />
                    Resend Code
                  </>
                )}
              </Button>
            </div>
          )}
          
          {step === 5 && (
            <Button onClick={() => setIsOpen(false)} className="w-full">
              Done
            </Button>
          )}
        </DialogFooterSticky>
      </DialogContent>
    </Dialog>
  );
}
